/**
 * AnalyticsFilterBar — shared top-of-dashboard filter row.
 *
 * Three controls (last one optional):
 *   - Date range:  preset dropdown (DATE_RANGE_PRESETS from useDashboardFilters)
 *   - Organization: multi-select popover (driven by an `orgs` prop — the page
 *                   passes its scoped list of available orgs)
 *   - Node:        optional multi-select popover (only Dashboards 2 + 4 use this)
 *
 * State is read/written via useDashboardFilters → URL params. No internal
 * Zustand store. Cross-widget filtering on the dashboard happens because every
 * widget reads the same hook and rerenders when search params change.
 *
 * GoodData also supports custom date ranges + granularity controls; that detail
 * lands when a widget needs it (defer until Week 3+).
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar, Filter, RotateCcw, Search, X } from '@/utils/icons';
import {
  useDashboardFilters,
  DATE_RANGE_PRESETS,
  DATE_RANGE_GROUPS,
  type DateRange,
} from '@/hooks/useDashboardFilters';

interface OrgOption {
  id: string;
  name: string;
}

interface NodeOption {
  id: string;
  name: string;
}

interface AnalyticsFilterBarProps {
  /** Orgs the user can filter by. Empty array hides the org selector. */
  orgs?: OrgOption[];
  /** Nodes the user can filter by. Empty array (or omitted) hides the node selector. */
  nodes?: NodeOption[];
  /** Show the "Reset" button. Default true. */
  showReset?: boolean;
}

function dateRangeLabel(range: DateRange): string {
  if (range.kind === 'all-time') return 'All time';
  if (range.kind === 'static') return `${range.from} → ${range.to}`;
  const unitLabel: Record<string, string> = {
    year: 'year', quarter: 'quarter', month: 'month', week: 'week', day: 'day', hour: 'hour', minute: 'minute',
  };
  return `Last ${range.count} ${unitLabel[range.unit] ?? range.unit}${range.count !== 1 ? 's' : ''}` +
    (range.excludeToday ? ' (excl. today)' : '');
}

export function AnalyticsFilterBar({ orgs = [], nodes = [], showReset = true }: AnalyticsFilterBarProps) {
  const { dateRange, orgs: selectedOrgs, nodes: selectedNodes, setDateRange, setOrgs, setNodes, reset } = useDashboardFilters();

  // Date range preset matching — write a stable preset key into the Select value.
  const activePresetIndex = DATE_RANGE_PRESETS.findIndex((p) => JSON.stringify(p.value) === JSON.stringify(dateRange));
  const presetValue = activePresetIndex >= 0 ? String(activePresetIndex) : 'custom';

  return (
    <div className="dls-flex dls-flex-wrap dls-items-center dls-gap-3 dls-px-4 dls-py-3 dls-bg-card dls-border dls-border-border dls-rounded-lg">
      {/* Date range — grouped presets matching GoodData's granularity buckets. */}
      <div className="dls-flex dls-items-center dls-gap-2">
        <Calendar className="dls-h-4 dls-w-4 dls-text-muted-foreground" />
        <Select
          value={presetValue}
          onValueChange={(v) => {
            if (v === 'custom') return;
            const preset = DATE_RANGE_PRESETS[Number.parseInt(v, 10)];
            if (preset) setDateRange(preset.value);
          }}
        >
          <SelectTrigger className="dls-w-56 dls-h-8">
            <span className="dls-truncate">{dateRangeLabel(dateRange)}</span>
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGE_GROUPS.map((g) => {
              const items = DATE_RANGE_PRESETS.map((p, i) => ({ ...p, originalIndex: i })).filter((p) => p.group === g.key);
              if (items.length === 0) return null;
              return (
                <div key={g.key} className="dls-pb-1">
                  <div className="dls-px-2 dls-pt-2 dls-pb-0.5 dls-text-[10px] dls-font-semibold dls-uppercase dls-tracking-wider dls-text-muted-foreground/60">
                    {g.label}
                  </div>
                  {items.map((p) => (
                    <SelectItem key={`${g.key}-${p.label}-${p.originalIndex}`} value={String(p.originalIndex)}>
                      {p.label}
                    </SelectItem>
                  ))}
                </div>
              );
            })}
            {activePresetIndex < 0 && (
              <SelectItem value="custom" disabled>
                Custom range (set via URL)
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        {/* Exclude today — only applies to relative ranges. Toggles the flag
            on the active preset without changing count/unit. */}
        {dateRange.kind === 'relative' && (
          <label className="dls-flex dls-items-center dls-gap-1.5 dls-text-xs dls-text-muted-foreground dls-cursor-pointer dls-select-none">
            <Checkbox
              checked={dateRange.excludeToday}
              onCheckedChange={(checked) =>
                setDateRange({ ...dateRange, excludeToday: checked === true })
              }
              aria-label="Exclude today"
            />
            Exclude today
          </label>
        )}
      </div>

      {/* Organization multi-select */}
      {orgs.length > 0 && (
        <MultiSelectPopover
          label="Organization"
          icon={<Filter className="dls-h-4 dls-w-4 dls-text-muted-foreground" />}
          options={orgs}
          selected={selectedOrgs}
          onChange={setOrgs}
          emptyLabel="All organizations"
        />
      )}

      {/* Node multi-select (optional) */}
      {nodes.length > 0 && (
        <MultiSelectPopover
          label="Node Name"
          icon={<Filter className="dls-h-4 dls-w-4 dls-text-muted-foreground" />}
          options={nodes}
          selected={selectedNodes}
          onChange={setNodes}
          emptyLabel="All nodes"
          searchable
        />
      )}

      {/* Selected filter chips */}
      <FilterChips
        orgs={orgs}
        selectedOrgs={selectedOrgs}
        onClearOrg={(id) => setOrgs(selectedOrgs.filter((s) => s !== id))}
        nodes={nodes}
        selectedNodes={selectedNodes}
        onClearNode={(id) => setNodes(selectedNodes.filter((s) => s !== id))}
      />

      {/* Reset */}
      {showReset && (dateRange.kind !== 'all-time' || selectedOrgs.length > 0 || selectedNodes.length > 0) && (
        <Button variant="ghost" size="sm" onClick={reset} className="dls-ml-auto">
          <RotateCcw className="dls-h-3.5 dls-w-3.5 dls-mr-1.5" />
          Reset
        </Button>
      )}
    </div>
  );
}

interface MultiSelectPopoverProps {
  label: string;
  icon?: React.ReactNode;
  options: Array<{ id: string; name: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
  emptyLabel: string;
  searchable?: boolean;
}

function MultiSelectPopover({ label, icon, options, selected, onChange, emptyLabel, searchable }: MultiSelectPopoverProps) {
  const [search, setSearch] = useState('');
  const filtered = searchable && search
    ? options.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;
  const allSelected = selected.length === 0;
  const triggerLabel = allSelected ? emptyLabel : `${label} (${selected.length})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="secondary" size="sm" className="dls-h-8 dls-gap-2">
          {icon}
          <span>{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="dls-w-72 dls-p-0" align="start">
        <div className="dls-px-3 dls-py-2 dls-border-b dls-border-border dls-flex dls-items-center dls-justify-between">
          <span className="dls-text-xs dls-font-semibold dls-uppercase dls-tracking-wider dls-text-muted-foreground">
            {label}
          </span>
          {selected.length > 0 && (
            <button
              type="button"
              className="dls-text-xs dls-text-neeve-primary hover:dls-underline"
              onClick={() => onChange([])}
            >
              Clear all
            </button>
          )}
        </div>
        {searchable && (
          <div className="dls-p-2 dls-border-b dls-border-border">
            <div className="dls-relative">
              <Search className="dls-absolute dls-left-2 dls-top-1/2 dls--translate-y-1/2 dls-h-3.5 dls-w-3.5 dls-text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="dls-h-7 dls-pl-7 dls-text-sm"
              />
            </div>
          </div>
        )}
        <div className="dls-max-h-64 dls-overflow-y-auto dls-py-1">
          {filtered.length === 0 ? (
            <div className="dls-px-3 dls-py-4 dls-text-sm dls-text-muted-foreground dls-text-center">
              No options
            </div>
          ) : (
            filtered.map((option) => {
              const isChecked = selected.includes(option.id);
              return (
                <label
                  key={option.id}
                  className="dls-flex dls-items-center dls-gap-2 dls-px-3 dls-py-1.5 dls-text-sm hover:dls-bg-muted dls-cursor-pointer"
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      if (checked) onChange([...selected, option.id]);
                      else onChange(selected.filter((id) => id !== option.id));
                    }}
                  />
                  <span className="dls-truncate">{option.name}</span>
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface FilterChipsProps {
  orgs: OrgOption[];
  selectedOrgs: string[];
  onClearOrg: (id: string) => void;
  nodes: NodeOption[];
  selectedNodes: string[];
  onClearNode: (id: string) => void;
}

function FilterChips({ orgs, selectedOrgs, onClearOrg, nodes, selectedNodes, onClearNode }: FilterChipsProps) {
  const orgMap = new Map(orgs.map((o) => [o.id, o.name]));
  const nodeMap = new Map(nodes.map((n) => [n.id, n.name]));
  if (selectedOrgs.length === 0 && selectedNodes.length === 0) return null;

  return (
    <div className="dls-flex dls-flex-wrap dls-items-center dls-gap-1.5">
      {selectedOrgs.map((id) => (
        <Badge key={`org-${id}`} variant="secondary" className="dls-gap-1">
          {orgMap.get(id) ?? id}
          <button
            type="button"
            onClick={() => onClearOrg(id)}
            className="dls-text-muted-foreground hover:dls-text-foreground"
            aria-label={`Remove ${orgMap.get(id) ?? id}`}
          >
            <X className="dls-h-3 dls-w-3" />
          </button>
        </Badge>
      ))}
      {selectedNodes.map((id) => (
        <Badge key={`node-${id}`} variant="secondary" className="dls-gap-1">
          {nodeMap.get(id) ?? id}
          <button
            type="button"
            onClick={() => onClearNode(id)}
            className="dls-text-muted-foreground hover:dls-text-foreground"
            aria-label={`Remove ${nodeMap.get(id) ?? id}`}
          >
            <X className="dls-h-3 dls-w-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}
